import {
    defineComponent,
    ref,
    watch,
    onMounted,
    computed,
    nextTick,
    onUnmounted,
    useTemplateRef,
    onBeforeMount, reactive
} from "vue";
import Guid from "@/app/shared/helpers/guid";
import DateUtil from "@/app/shared/helpers/date-util";
import * as KlipApi from '@/api/klip-api.proxy';
import {castArray, debounce, first, isEmpty, isEqual} from "lodash-es";
import OrganisationHelper from "@/app/shared/helpers/organisation-helper";
import KlLocationpicker from '@/app/shared/components/kl-locationpicker/kl-locationpicker.vue';
import {ILocationPickerGeometry} from '@/app/shared/components/kl-locationpicker/kl-locationpicker';
import {useIsMobile} from '@/app/shared/composables/useIsMobile';

export class MapRequestSearchField {
    searchField: string;
    keyword: string;
    date: {
        from: Date;
        to: Date;
    };
    checkWorkingPeriod?: boolean;
    searchOption?: string;
    organisationId?: string;
    location?: any; // TODO: betere typecheck!!
    archive?: boolean;
    tags?: string[];
}

// replacement for:
// isNaN(+searchFields.searchOption) ? KlipApi.SearchOption.SubOrganisation : +searchFields.searchOption as unknown as KlipApi.SearchOption
export function castStringToSearchOption(searchOption: string, defaultOption: KlipApi.SearchOption): KlipApi.SearchOption {
    if (!searchOption) {
        return defaultOption;
    }
    if (isNaN(+searchOption)) {
        console.warn('Invalid argument: searchOption should be guid or castable to number', searchOption);
        // TODO: opgelet, dit veld wordt ook gebruikt om organisationIds door te sturen..
        // in dat geval wordt de KlipApi.IGetMapRequestsInput.organisationId ook ingevuld
        return defaultOption;
    }
    return +searchOption as unknown as KlipApi.SearchOption;
}

// replacement for:
// KlipApi.SearchField[searchFields.searchField as keyof typeof KlipApi.SearchField]
export function castStringToSearchField(searchField: string): KlipApi.SearchField {
    if (!searchField) {
        return KlipApi.SearchField.None;
    }
    return KlipApi.SearchField[searchField as keyof typeof KlipApi.SearchField];
}

enum ESearchArchiveMode {
    Active = 'Active',
    Archived = 'Archived'
}

enum ESearchDateMode {
    DateReceived = 'DateReceived',
    PeriodOverlap = 'PeriodOverlap'
}

interface IMaprequestSearchData {
    keyword: string;
    searchField: string;
    searchOption: string;
    searchArchive: ESearchArchiveMode;
    location: ILocationPickerGeometry;
    tagsChecked: boolean;
    searchTags: string[];
    dateFrom: Date[];
    dateTo: Date[];
    dateMode: ESearchDateMode;
}

// outside component to ensure we can always use it during c'tor
function _toSearchData(filter: MapRequestSearchField): IMaprequestSearchData {
    const _defaultSearchField = KlipApi.SearchField[KlipApi.SearchField.MyReference]; // = the string 'MyReference'

    const result: IMaprequestSearchData = {
        keyword: filter?.keyword || '',
        searchField: filter?.searchField || _defaultSearchField,
        searchOption: filter?.organisationId || filter?.searchOption,
        searchArchive: !!filter?.archive ? ESearchArchiveMode.Archived : ESearchArchiveMode.Active,
        location: filter?.location || null,
        tagsChecked: filter?.tags ? !isEmpty(filter.tags) : false,
        searchTags: filter?.tags ? castArray(filter.tags) : [],
        dateFrom: filter?.date?.from ? [filter.date.from] : [],
        dateTo: filter?.date?.to ? [filter.date.to] : [],
        dateMode: !!filter?.checkWorkingPeriod ? ESearchDateMode.PeriodOverlap : ESearchDateMode.DateReceived,
    }

    return result;
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlSearchMaprequests',
    emits: ['search'],
    props: {
        defaultSearchOption: {
            type: String,
            default: null,
            required: false,
        },
        searchFields: {
            type: Array<ICodeListValueOrder>,
            required: true,
        },
        searchOptions: {
            type: Array<ICodeListValueOrder>,
            default: null,
            required: false,
        },
        groupedSearchOptions: {
            type: Array<ICodeListValueOrder>,
            default: null,
            required: false,
        },
        tags: {
            type: Array<string>,
            default: null,
            required: false,
        },
        // dit wordt de filter na een reset
        defaultFilter: {
            type: Object as () => MapRequestSearchField,
            default: null,
            required: false,
        },
        currentFilter: {
            type: Object as () => MapRequestSearchField,
            default: null,
            required: false,
        },
        modSimple: {
            type: Boolean,
            required: false,
            default: false
        },
        onlyActive: {
            type: Boolean,
            required: false,
            default: false
        },
        debounceWait: {
            type: Number,
            default: 500
        },
        useDateMode: {
            type: Boolean,
            default: false
        },
    },
    setup(props, {emit}) {

        const searchArchiveOptions = [
            {
                key: ESearchArchiveMode.Active,
                label: 'Actieve planaanvragen (< 6 maanden)',
            }, {
                key: ESearchArchiveMode.Archived,
                label: 'Gearchiveerde planaanvragen (> 6 maanden)',
            },
        ];

        const dateModeOptions = [
            {
                key: ESearchDateMode.DateReceived,
                label: 'Aanvraagdatum',
            }, {
                key: ESearchDateMode.PeriodOverlap,
                label: 'Periode valt (deels) in',
            },
        ]


        const searchData = ref<IMaprequestSearchData>(_toSearchData(null));
        const isMobile = useIsMobile();

        const locationpicker = useTemplateRef<InstanceType<typeof KlLocationpicker>>('locationpicker');

        const _initSearchData = () => {

            searchData.value = _toSearchData(props.defaultFilter);

            searchData.value.keyword = props.defaultFilter?.keyword;
            searchData.value.searchArchive = ESearchArchiveMode.Active;

            if (props.searchOptions) {
                searchData.value.searchOption = props.defaultFilter?.organisationId || props.defaultFilter?.searchOption;
            }

            if (hasTags.value) {
                searchData.value.tagsChecked = false;
                searchData.value.searchTags = [];
            }
        }

        const hasTags = computed(() => {
            return props.tags && props.tags.length >= 0;
        });

        const keyWordSearchWidth = computed(() => {
            return props.searchFields && props.searchFields.length <= 3 ? "4" : "3"
        });

        const dateFromMaxDate = computed((): string => {
            return DateUtil.formatDate(first(searchData.value.dateTo));
        });

        const dateToMinDate = computed((): string => {
            return DateUtil.formatDate(first(searchData.value.dateFrom));
        });

        const uniqueFieldId = (id: string) => {
            return id + Guid.randomGuid();
        };

        const setDepth = (depth: number, name: string) => {
            return OrganisationHelper.setDepth(depth, name);
        }

        // enable tags & set focus
        const clickTags = async () => {
            searchData.value.tagsChecked = true;
            await nextTick();
            const searchTagsField = document.getElementsByName("searchTags");
            if (searchTagsField && searchTagsField.length) {
                searchTagsField[0].focus();
            }
        }

        const _search = () => {
            const input: IMaprequestSearchData = searchData.value;

            const searchOptionIsGuid = Guid.isGuid(input.searchOption);
            const data: MapRequestSearchField = {
                searchField: input.searchField as keyof typeof KlipApi.SearchField,
                keyword: input.keyword,
                date: {
                    from: first(input.dateFrom),
                    to: first(input.dateTo),
                },
                checkWorkingPeriod: input.dateMode === ESearchDateMode.PeriodOverlap,
                searchOption: searchOptionIsGuid ? null : input.searchOption,
                organisationId: searchOptionIsGuid ? input.searchOption : null,
                location: input.location,
                archive: input.searchArchive === ESearchArchiveMode.Archived,

                // tagsActive is computed depending on tagsChecked
                tags: input.searchTags,
            }

            //console.log('emit(search)', data, input);
            emit('search', data);
        }

        const _debouncedSearch = debounce(() => {
            _search();
        }, props.debounceWait);

        const reset = () => {
            _initSearchData();

            if (locationpicker.value) {
                locationpicker.value.removeLocation();
            }
        }

        const switchToArchived = () => {
            searchData.value.searchArchive = ESearchArchiveMode.Archived;
        }

        const _onFilterChanged = (filter: MapRequestSearchField) => {
            searchData.value = _toSearchData(filter);
        };

        const _initFilter = (filter: MapRequestSearchField) => {
            searchData.value = _toSearchData(filter);
        };

        watch(
            searchData,
            (newValue, oldValue) => {
                _debouncedSearch();
            },
            {immediate: false, deep: true}
        );

        watch(
            () => props.defaultFilter,
            (val, oldVal) => {
                if (props.currentFilter) {
                    // TODO: legacy check: vroeger kon de defaultFilter wijzigen (bv door toepassen van query/localstorage)
                    // die cases gebruiken normaal geen 'props.currentFilter'
                    //throw new Error('kl-search-maprequest: defaultFilter should not change!');
                    console.warn('kl-search-maprequest: defaultFilter should not change!', val, oldVal);

                    return;
                }
                if (isEqual(_toSearchData(val), searchData.value)) {
                    // TO VERIFY: dit is een oude optimalisatie, denkelijk om verkeerd gebruik van de defaultFilter recht te trekken
                    // defaultFilter hoort in de huidige opzet gevuld te worden met de 'result.defaultSearchFilter' van de bff
                    console.warn('kl-search-maprequest: verify props.defaultFilter');
                    return;
                }
                _onFilterChanged(val);
            },
            {immediate: false, deep: true});

        watch(
            () => props.currentFilter,
            (val, oldVal) => {
                if (isEqual(_toSearchData(val), searchData.value)) {
                    // would result in the same 'searchData' = probable round trip of emit('search')
                    return;
                }

                _onFilterChanged(val);
            },
            {immediate: false, deep: true});


        onBeforeMount(() => {
            _initSearchData();

            const filterData = {
                ...props.defaultFilter,
                ...props.currentFilter,
            };

            _initFilter(filterData);
        });

        onUnmounted(() => {
            _debouncedSearch.cancel();
        });

        return {
            searchData,

            hasTags,
            locationpicker,
            keyWordSearchWidth,
            uniqueFieldId,
            searchArchiveOptions,
            dateModeOptions,
            dateFromMaxDate,
            dateToMinDate,
            setDepth,
            clickTags,
            isMobile,

            // external components calls
            reset,
            switchToArchived,
        };
    }
});
